import { Alert, ArrowIcon } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { HashLink } from "react-router-hash-link";
import { SpapPlan } from "../../@types";
import { getPapPlansForState } from "../../api";
import routes from "../../app/routes";
import {
  AppPage,
  FocusableHeader,
  LoadingMask,
  PrintButton,
  StateSelector,
} from "../../components";
import { sendTealiumEvent, useTranslate } from "../../helpers";
import states from "../EnrollmentFormPage/states";
import SpapPlanCard from "./SpapPlanCard";

interface SpapPlansMatchParams {
  state: string;
}

const SpapPlansPage: FunctionComponent<RouteComponentProps> = ({
  match,
  location,
}) => {
  const t = useTranslate();
  const [plans, setPlans] = useState<SpapPlan[]>([]);
  const [stateAbbr, setStateAbbr] = useState(
    (match.params as SpapPlansMatchParams).state
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchPlans = async () => {
    setLoading(true);
    setError("");
    try {
      const programs = await getPapPlansForState(stateAbbr);
      setLoading(false);
      setPlans(programs);
    } catch (_e) {
      setError(t("pap.plans.error"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [stateAbbr]);

  useEffect(() => {
    setStateAbbr((match.params as SpapPlansMatchParams).state);
  }, [match.params]);

  return (
    <AppPage hideLogin={true} innerClassName="SpapPlansPage">
      <LoadingMask loading={loading} delay={500} />
      <div className="SpapPlansPage__banner ds-u-fill--gray-lightest ds-u-display--flex ds-u-justify-content--between ds-u-flex-wrap--wrap mct-hide-print ds-u-padding--2 ds-u-md-padding-x--7 ds-u-md-padding-y--3 ds-u-margin-bottom--2">
        <StateSelector
          buttonText={t("spap.state_selector.button_text")}
          defaultLabel={t("spap.state_selector.select_state")}
          label={t("spap.state_selector.label")}
          defaultValue={stateAbbr}
          programType="SPAP"
        />
        <PrintButton
          tealiumProps={{
            event_action: "Print",
            event_label: window.location.hash,
            additional_props: { event_category: "SPAP" },
          }}
        />
      </div>
      <div className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
        <HashLink
          to={`${routes.spap.landingPage}${location.search}`}
          onClick={(): void => {
            sendTealiumEvent("Back to search", window.location.hash, {
              event_category: "SPAP",
            });
          }}
          className="mct-hide-print ds-c-button ds-c-button--transparent ds-u-text-decoration--none ds-u-padding-left--0 ds-u-margin-bottom--3"
        >
          <ArrowIcon
            direction="left"
            className="ds-u-valign--middle ds-u-margin-right--1"
          />
          {t("pap.plans.go_back")}
        </HashLink>
        <FocusableHeader
          className="ds-text-heading--2xl"
          text={
            error || !stateAbbr
              ? t("spap.plans.select_state")
              : states.find(state => stateAbbr === state.abbreviation)?.name ||
                ""
          }
        />
      </div>
      <div className="SpapPlansPage__content">
        {error && (
          <div className="ds-u-margin-bottom--2">
            <Alert variation="error" role="alert">
              {error}
            </Alert>
          </div>
        )}
        <ul
          className="ds-c-list--bare"
          role="list"
          data-testid="e2e-program-results"
        >
          {plans.map(plan => {
            return (
              <li key={plan.program_name} role="listitem">
                <SpapPlanCard plan={plan} />
              </li>
            );
          })}
        </ul>
      </div>
    </AppPage>
  );
};

export default withRouter(SpapPlansPage);
